import { defineComponent as _defineComponent } from 'vue'
import { PropType, computed, inject, onMounted, ref } from "vue";
import {
  IBookableDatesInfo,
  IDateInfo,
  IKakaoPayBookInfo,
} from "@/domain/entities/book";
import BaseButton from "@/components/shared/button/base/BaseButton.vue";
import { IPaymentMethod } from "@/domain/entities/payment/paymentMethod.types";
import {
  BookIntervalCode,
  ERROR_CODE_BOOK_BLOCKED,
  GetMonthlyBookableDatesWithSeat,
  KCPResult,
  PGCode,
  PROVIDE_KEY_GET_MONTHLY_BOOKABLE_DATES_WITH_SEAT,
  PROVIDE_KEY_REMOVE_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_MONTHLY_BOOK_DETAIL,
  RemoveLoadingImage,
  RouterName,
  ShowErrorModal,
  ShowLoadingImage,
  ShowMonthlyBookDetail,
} from "@/types";
import { usePaymentStore } from "@/stores/paymentStore";
import PaymentInfoSection from "./PaymentInfoSection.vue";
import { useServiceStore } from "@/stores/serviceStore";
import useCurrencyFormat from "@/use/useCurrencyFormat";
import Modal from "@/components/shared/modal/base/Modal.vue";
import BookKcp from "../BookKcp.vue";
import router from "@/router";
import useGetBookRequestParams from "@/use/useGetBookRequestParams";
import deviceHelper from "@/utils/deviceInfo";
import useResizeWebviewWithSafeArea from "@/use/useResizeWebviewWithSafeArea";
import { IResultData } from "@/domain/entities/shared/result";
import { useBookStore } from "@/stores/bookStore";
import { useUserStore } from "@/stores/userStore";


const __default__ = {
  name: "PaymentFooter",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  turnId: {
    type: String,
    required: true,
  },
  getOnBusStopId: {
    type: String,
    required: true,
  },
  getOffBusStopId: {
    type: String,
    required: true,
  },
  bookingDates: {
    // 예약 진행중인 날짜
    type: Array as PropType<Array<IDateInfo>>,
  },
  oneTimeFare: {
    type: Number,
  },
  totalAmount: {
    type: Number,
  },
  autoSeatAssign: {
    type: Boolean,
    default: false,
  },
  seatsSelectable: {
    type: Boolean,
  },
  currentBookInterval: {
    type: String as PropType<BookIntervalCode>,
  },
  shouldCheckSeatAvailability: {
    type: Boolean,
    default: false,
  },
},
  setup(__props, { expose: __expose }) {
  __expose();

const props = __props;

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showLoadingImage = inject(
  PROVIDE_KEY_SHOW_LOADING_IMAGE
) as ShowLoadingImage;
const removeLoadingImage = inject(
  PROVIDE_KEY_REMOVE_LOADING_IMAGE
) as RemoveLoadingImage;
const getMonthlyBookableDatesWithSeat = inject(
  PROVIDE_KEY_GET_MONTHLY_BOOKABLE_DATES_WITH_SEAT,
  () => {}
) as GetMonthlyBookableDatesWithSeat;
const showMonthlyBookDetail = inject(
  PROVIDE_KEY_SHOW_MONTHLY_BOOK_DETAIL
) as ShowMonthlyBookDetail;

const bookStore = useBookStore();
const paymentStore = usePaymentStore();
const serviceStore = useServiceStore();
const serviceInfo = computed(() => serviceStore.serviceInfo);
const paymentTimeoutMin = computed(
  () => serviceInfo.value?.bookTimeoutIntervalMin ?? 0
);
const shouldShowUserRemainBookCount = computed(
  () => !!serviceInfo.value?.shouldShowRemainBookCount
);

const userStore = useUserStore();

const bookKcpRef = ref<InstanceType<typeof BookKcp>>();
const bookInProgress = ref(false);
/** 중복좌석으로 월예약 불가시 보여주는 모달에서 자동좌석배정 선택 여부 */
const isAutoSeatAssignSelectedInModal = ref(false);
const autoSeatAssign = computed(
  () => props.autoSeatAssign || isAutoSeatAssignSelectedInModal.value
);

/** 예약제한 상태 여부 */
const isBookBlocked = computed(() => userStore.isBookBlocked);

/** 결제 방법 조회하기(KCP, 카카오페이, 무료) */
const paymentMethods = ref<IPaymentMethod[]>([]);
const getPaymentMethods = async () => {
  const paymentMethodsResult = await paymentStore.getPaymentMethods(
    props.turnId
  );

  if (!paymentMethodsResult.isSuccess) {
    showErrorModal(
      "결제방법 조회 오류",
      paymentMethodsResult.errorMessage,
      paymentMethodsResult.resultCode,
      paymentMethodsResult.errorTime
    );
    return;
  }

  paymentMethods.value = paymentMethodsResult.data ?? [];
};

// 결제 여부 판단하는 플래그 값
const shouldPay = computed(
  () =>
    !paymentMethods.value?.find(
      (paymentMethod) => paymentMethod.pgCode === PGCode.FREE
    )
);

const isBookableState = () =>
  paymentStore.isBookableState(
    props.totalAmount,
    bookInProgress.value,
    props.seatsSelectable,
    props.bookingDates ?? [],
    shouldPay.value
  );

// 특정 좌석에 대한 월예약 불가 모달 처리
const shouldShowHasUnavaliableSeatModal = ref(false);
const toggleShowHasUnavaliableSeatModal = (show: boolean) => {
  shouldShowHasUnavaliableSeatModal.value = show;
};

/** 자동좌석 배정으로 월예약 진행 */
const processMonthlyBookWithAutoSeatAssign = () => {
  isAutoSeatAssignSelectedInModal.value = true;
  handleProcessPayment();
};

const selectedPgCode = ref();
const onClickPaymentButton = async (pgCode: PGCode) => {
  if (!isBookableState()) return;

  selectedPgCode.value = pgCode;

  if (props.shouldCheckSeatAvailability) {
    if (!(await isValidMonthlyBookableDates())) {
      toggleShowHasUnavaliableSeatModal(true);
      return;
    }
  }

  handleProcessPayment();
};

const bookableDatesInfos = ref<IBookableDatesInfo[]>([]);
const isValidMonthlyBookableDates = async () => {
  const bookableDatesInfosResult = await getMonthlyBookableDatesWithSeat();
  bookableDatesInfos.value = bookableDatesInfosResult ?? [];

  return (
    bookableDatesInfosResult?.[0]?.bookableDates.every(
      (date) => !!date.targetSeatBookableYn
    ) ?? false
  );
};

const handleProcessPayment = () => {
  if (
    selectedPgCode.value === PGCode.KCP ||
    selectedPgCode.value === PGCode.KakaoPay
  ) {
    shouldShowPaymentTimeoutModal.value = true;
  } else {
    toggleShowPaymentTimeoutModal(false);
    processPayment();
  }
};

/** 결제 요청 후 n분 이내 결제해야 한다는 확인 팝업처리 */
const shouldShowPaymentTimeoutModal = ref(false);
const toggleShowPaymentTimeoutModal = (show: boolean) => {
  shouldShowPaymentTimeoutModal.value = show;
};

const processPayment = () => {
  if (selectedPgCode.value === PGCode.KCP) {
    processCreditCardPayment();
  } else if (selectedPgCode.value === PGCode.KakaoPay) {
    processKakaoPay();
  } else if (selectedPgCode.value === PGCode.FREE) {
    processFreeBook();
  } else {
    console.error("결제 방법이 올바르지 않습니다", selectedPgCode.value);
  }
  selectedPgCode.value = undefined;
  isAutoSeatAssignSelectedInModal.value = false;
};

const handleError = (title: string, result: any) => {
  showErrorModal(
    title,
    result.errorMessage,
    result.resultCode,
    result.errorTime
  );

  // 노쇼 페널티 등으로 예약이 불가능한 상황일때 유저정보 갱신
  if (result.resultCode === ERROR_CODE_BOOK_BLOCKED) {
    userStore.getUserInfo();
  }
};

const getUserRemainBookCount = async () => {
  if (!shouldShowUserRemainBookCount.value) return;

  const result = await userStore.getRemainBookCount();

  if (!result.isSuccess) {
    handleError("잔여예약 횟수 조회 오류", result);
    return;
  }
};

const handleBookingSuccess = (bookUid: string) => {
  getUserRemainBookCount();
  moveToBookConfirmation(bookUid);
};

/** KCP 결제 요청하기
 * 예약시간 만료로 인해 예약이 실패된 경우
 * - PC 결제의 경우 onClickBookComplete 함수에서 에러 팝업 처리
 * - Mobile 결제의 경우 BookFail로 이동 후 query에 resultCode가 408100001로 들어옴
 */
const processCreditCardPayment = () => {
  const bookRequestParams = useGetBookRequestParams(
    autoSeatAssign.value,
    props.seatsSelectable,
    props.turnId,
    props.getOnBusStopId,
    props.getOffBusStopId,
    props.bookingDates ?? [],
    props.currentBookInterval === BookIntervalCode.MONTH
  );

  bookInProgress.value = true;
  showLoadingImage();
  bookKcpRef.value?.kcpSubmit(bookRequestParams);
};

// PC에서 KCP 결제 클릭했을 때 openKcp 함수 호출
window.openKCP = () => {
  bookInProgress.value = false;
  removeLoadingImage();
};

// PC에서 KCP 결제 완료 후 처리
const onCompletedKcpBook = async (result: KCPResult) => {
  const { bookUid, resultCode, resultMessage, ordr_idxx: payUid } = result;
  bookInProgress.value = false;
  removeLoadingImage();

  if (resultCode === "0" && bookUid) {
    handleBookingSuccess(bookUid);
    return;
  }

  await bookStore.failKcp(payUid, bookUid);
  showErrorModal("KCP 결제 오류", `[${resultCode}] ${resultMessage}`);
};

// 공통된 예약 프로세스를 처리하는 함수
const processBooking = async (
  bookingFunction: (
    autoSeatAssign: boolean,
    seatsSelectable: boolean,
    turnId: string,
    getOnBusStopId: string,
    getOffBusStopId: string,
    bookingDates: IDateInfo[],
    isMonthly: boolean
  ) => Promise<IResultData<any>>,
  successCallback: (data: any) => void,
  errorCallback: (result: any) => void
) => {
  bookInProgress.value = true;
  showLoadingImage();

  const bookingResult = await bookingFunction(
    autoSeatAssign.value,
    props.seatsSelectable,
    props.turnId,
    props.getOnBusStopId,
    props.getOffBusStopId,
    props.bookingDates ?? [],
    props.currentBookInterval === BookIntervalCode.MONTH
  );

  bookInProgress.value = false;
  removeLoadingImage();

  if (bookingResult.isSuccess) {
    successCallback(bookingResult.data);
  } else {
    errorCallback(bookingResult);
  }
};

/** 카카오페이 결제 요청하기
 * 예약시간 만료로 인해 예약이 실패된 경우
 * - PC 결제와 Mobile 결제 모두 BookSuccess로 이동 후 예약승인 API에서 오류코드 408100001 반환
 */
const processKakaoPay = (): void => {
  const successCallback = (data: IKakaoPayBookInfo) => {
    useResizeWebviewWithSafeArea(true);

    const kakaoPayUrl = deviceHelper.isMobile()
      ? data?.mobileRedirectUrl ?? ""
      : data?.pcRedirectUrl ?? "";
    window.location.href = kakaoPayUrl;
  };

  processBooking(
    paymentStore.requestKakaoPay,
    successCallback,
    (result: IResultData<IKakaoPayBookInfo>) => {
      handleError("카카오페이 결제 오류", result);
    }
  );
};

// 무료 예약을 처리하는 함수
const processFreeBook = (): void => {
  processBooking(
    paymentStore.requestFreeBook,
    (data: string) => {
      handleBookingSuccess(data ?? "");
    },
    (result: IResultData<string>) => {
      handleError("예약 오류", result);
      if (!result.data) return; // 임시 무료 예약 실패시 bookUid가 생성되지 않으므로 무료 예약 실패 API를 호출할 필요 없음
      paymentStore.requestFailFreeBook(result.data);
    }
  );
};

const moveToBookConfirmation = (bookUid: string) => {
  router.push({ name: RouterName.BookConfirmation, query: { bookUid } });
};

onMounted(async () => {
  await getPaymentMethods();
});

const __returned__ = { props, showErrorModal, showLoadingImage, removeLoadingImage, getMonthlyBookableDatesWithSeat, showMonthlyBookDetail, bookStore, paymentStore, serviceStore, serviceInfo, paymentTimeoutMin, shouldShowUserRemainBookCount, userStore, bookKcpRef, bookInProgress, isAutoSeatAssignSelectedInModal, autoSeatAssign, isBookBlocked, paymentMethods, getPaymentMethods, shouldPay, isBookableState, shouldShowHasUnavaliableSeatModal, toggleShowHasUnavaliableSeatModal, processMonthlyBookWithAutoSeatAssign, selectedPgCode, onClickPaymentButton, bookableDatesInfos, isValidMonthlyBookableDates, handleProcessPayment, shouldShowPaymentTimeoutModal, toggleShowPaymentTimeoutModal, processPayment, handleError, getUserRemainBookCount, handleBookingSuccess, processCreditCardPayment, onCompletedKcpBook, processBooking, processKakaoPay, processFreeBook, moveToBookConfirmation, BaseButton, PaymentInfoSection, get useCurrencyFormat() { return useCurrencyFormat }, Modal, BookKcp }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})